import React, { useEffect, useState } from 'react';
import { Button, Label } from 'reactstrap';
import C from '../C';
import { downloadCSVRow } from '../base/components/CSVUtils.ts';
import LinkOut from '../base/components/LinkOut';
import Misc from '../base/components/Misc';
import { setNavContext } from '../base/components/NavBar';
import PropControl from '../base/components/PropControl';
import SavePublishDeleteEtc from '../base/components/SavePublishDeleteEtc';
import ShareWidget from '../base/components/ShareWidget';
import SimpleTable from '../base/components/SimpleTable';
import PropControlDataItem from '../base/components/propcontrols/PropControlDataItem';
import PropControlUpload from '../base/components/propcontrols/PropControlUpload';
import KStatus from '../base/data/KStatus';
import { setWindowTitle } from '../base/plumbing/Crud';
import DataStore, { getDataPath } from '../base/plumbing/DataStore';
import { asNum, encURI, sum } from '../base/utils/miscutils';
import ActionMan from '../plumbing/ActionMan';
import ListItems from './ListItems';


function PubListPage() {
	const type = C.TYPES.PubList;
	setWindowTitle(type);

	// const blocked = Misc.CheckAccess({can: 'editAdvert'});
	// if (blocked) return blocked;

	const path = DataStore.getValue(['location','path']);
	const itemId = path[1];
	

	if ( ! itemId) {
	return <ListItems type={type} base={{}} />;
	}
	const pvItem = ActionMan.getDataItem({type:C.TYPES.PubList, id:itemId, status:KStatus.DRAFT});
	if ( ! pvItem.value) {
	return (<div><h1>PubList: {itemId}</h1><Misc.Loading /></div>);
	}
	const item = pvItem.value;
	setWindowTitle(item);	

	useEffect(() => {
	setNavContext(type, itemId, true);
	// setNavContext(C.TYPES.Advert, null);
	}, [itemId]);

	return (
	<div className="">
		<h1>PubList: {item.name || itemId}</h1>
		<EditPubList item={item} />
	</div>
	);
} // ./PubListPage

const handleGoBack = () => {
	const url = window.location.href;
	const urlList = url.split("/");
	if (urlList[urlList.length - 2] !== "#publist") return;
	urlList.pop(urlList.length - 1);
	window.location.assign(urlList.join("/"));
};

const isHideNull = () => {
	const hideNull = DataStore.getUrlValue('hide-null');
	return hideNull === 'true';
};

function EditPubList({item}) {
	const [itemData, setItemData] = useState();

	const hideNull = isHideNull();

	useEffect(() => {
	if (isHideNull()) {
		setItemData({...item, rows: item.rows.filter(row => row[2])});
	} else {
		setItemData(item);
	}
	}, [item, hideNull]);

	if (!itemData) {
	return <Misc.Loading />;
	}

	// render
	return (
	<div>
		<Button onClick={handleGoBack}>Go back</Button>
		<h3>{itemData.id===C.newId? 'Register New Publisher List' : 'Edit Publisher List'}</h3>
		<EditPubListForm item={itemData} />
		<ShareWidget item={itemData} hasButton />
		<LinkOut href="https://measurepublisher.good-loop.com/count/_top">Top 10</LinkOut>
	</div>
	);
}

const getEndPoint = (item, verb, force) => {
	return `/publist/${encURI(item.id)}?action=${verb}${force ? "&force=true" : ""}`;
};

/**
	 * @param {Object[]} rows [domain,impressions ie weight, kgco2pm ie value] Must all have values set
	 * @returns {number}
	 */
function weightedAverage(rows) {
	let wvs = rows.map(row => row[1]*row[2]);
	let ws = rows.map(row => row[1]);
	let swv = sum(wvs);
	let sw = sum(ws);
	let nani = wvs.findIndex(wv => isNaN(wv));
	if (nani !== -1) console.error("weightedAverage bad row ",rows[nani]);
	return swv / sw;
}

/**
 * @param {Object[]} rows 
 * @returns {number}
 */
const getEmptyRowsNumber = (rows) => {
	let count = 0;
	for (let i = 0; i < rows.length; i++) {
	if (Object.keys(rows[i]).length < 3) {
		count++;
	}
	}
	return count;
};

/**
 * @param {Object[]} rows 
 * @returns {string}
 */
const getCompletedPercentage = (rows) => {
	const emptyRows = getEmptyRowsNumber(rows);
	const percentage = (rows.length - emptyRows) * 100 / rows.length;
	return Math.round(percentage) + "%";
};

/**
 * @param {Object} param0 
 * @param {number[]} param0.rows format of rows: [domain,impressions,kgco2pm]
 * @param {string} param0.name
 * @returns {JSX.Element}
 */
const QuantileFilter = ({ rows, name = null }) => {
	// filter out partly unset rows (if we don't have a CO2pm score then we can't sort it into a quantile)
	rows = rows.filter(row => row[0] && row[1] && row[2]);
	// 0.25 / 0.75
	const [selectedQuantile, setSelectedQuantile] = useState(null);

	const quantileWordings = { 0.25: "Best", 0.75: "Worst" };

	/** @type {number[]} Just the CO2pm scores */
	const rowsWithCo2pm = rows.filter((row) => row[2]).map((row) => row[2]);

	if (rowsWithCo2pm.length === 0) {
		return <div className="mx-auto my-3" />;
	}

	/**
	 * @param {number[]} array kgCO2pm scores
	 * @param {number} q 0.25 (best) or 0.75 (worst)
	 * @returns {number} The threshold CO2pm for this quantile
	 */
	const getQuantile = (array, q) => {
		array.sort((a, b) => a - b);
		const pos = (array.length - 1) * q; // why -1??
		const base = Math.floor(pos);
		const rest = pos - base;
		if (array[base + 1] !== undefined) {
			return array[base] + rest * (array[base + 1] - array[base]);
		}
		return array[base];
	};

	const handleFilterChange = (event) => {
		setSelectedQuantile(asNum(event.target.value));
	};

	/**
	 * 
	 * @param {*} quantile 0.25/0.75
	 * @param {?boolean} exclude If truthy, return the excluded rows instead
	 * @returns 
	 */
	const rowsToDownload = (quantile, exclude) => {		
		if ( ! quantile) return rows;
		assert(quantile==0.25 || quantile==0.75,quantile);
		let threshold = getQuantile(rowsWithCo2pm, quantile);
		switch (quantile) {
			case 0.25:
				return rows.filter((row) => exclude? row[2] >= threshold : row[2] < threshold);
			case 0.75:
				return rows.filter((row) => exclude? row[2] <= threshold : row[2] > threshold);
			default:
				return rows;
		}
	};	
	let overallAverage = weightedAverage(rows);
	let qRows = rowsToDownload(selectedQuantile);
	let quantileAverage = weightedAverage(qRows);	
	let improvement;	
	if (selectedQuantile == 0.75) {
		let exRows = rowsToDownload(selectedQuantile, "exclude");
		let exAverage = weightedAverage(exRows);	
		improvement = (overallAverage - exAverage) / overallAverage;	
	} else {
		improvement = (overallAverage - quantileAverage) / overallAverage;	
	}

	return (
		<div className="mx-auto my-3">
			<span>Quantile: </span>
			<select value={selectedQuantile} onChange={handleFilterChange}>
				<option value="">None</option>
				<option value={0.25}>
					Best 25%: {getQuantile(rowsWithCo2pm, 0.25).toFixed(2)} kgCO2pm or lower
				</option>
				<option value={0.75}>
					Worst 25%: {getQuantile(rowsWithCo2pm, 0.75).toFixed(2)} kgCO2pm or higher
				</option>
			</select>
			<div>Overall Average: {overallAverage.toFixed(2)} kgCO2pm 
			Quantile Average: {quantileAverage.toFixed(2)} 
			Improvement: {Math.round(100*improvement)}%</div>

			{"\t"}

			{selectedQuantile && selectedQuantile !== "None" && (
				<Button
					onClick={() =>
						downloadCSVRow(
							rowsToDownload(selectedQuantile),
							`${name && name + "_"}${quantileWordings[selectedQuantile]}_25`
						)}
				>
					Download CSV for {quantileWordings[selectedQuantile]} 25% Publishers
				</Button>
			)}
		</div>
	);
};

/**
 * Check for the `domain, impressions` format
 * Refactor?? Probably best done server-side
 * @param {File} file 
 * @returns {boolean} true if the file 
 */
const validateCSV = async (file) => {
	const regex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

	const isValidURL = (string) => {
		try {
			return regex.test(string);
		} catch (_) {
				return false;
		}
	};

	const isValidNumber = (string) => {
		console.log(string, parseFloat(string), Number.isNaN(parseFloat(string)));
		return !Number.isNaN(parseFloat(string));
	};

	/** @type {boolean} */
	const passValidation = await new Promise((resolve) => {
		if (file.type !== 'text/csv' || !file.name.endsWith('.csv')) {
			resolve(false);
		}

		const reader = new FileReader();

		reader.onload = function (e) {
			const content = e.target.result;
			const rows = content.split('\n').map(row => row.split(','));

			// NB: start at i=1 to skip the header row
			for (let i = 1; i < rows.length; i++) {
				const row = rows[i];
				if ( ! row.length) continue; // skip blank rows
				if (row.length !== 2 || !isValidURL(row[0]) || !isValidNumber(row[1])) {
					console.warn("Validation failed for row "+i,row,file);
					resolve(false);
					return;
				}
			}
			resolve(true);
		};

		reader.onerror = function (err) {
			console.error("reader-error",err);
			resolve(false);
		};

		reader.readAsText(file);
	});

	return passValidation;
};

/**
 * Quick hack: reload page if upload sucess
 * @param {Object} obj
 */
const handleUpload = (obj) => {
	if (obj.response?.success) {
		window.location.reload();
	}
};

function EditPubListForm({item}) {
	let path = getDataPath({status:KStatus.DRAFT, type:C.TYPES.PubList, id:item.id});
	// A bit of a hack but since we're using hash we can't just get searchparams
	const debugMode = window.location.href.split("?")[1]?.toLowerCase().includes("debug=true"); 

	return (
	<div className="form">
		<PropControl inline type="toggle" prop="hide-null" dflt="false" left={{ label: "Show null", value: "false", colour: "primary" }} right={{ label: "Hide null", value: "true", colour: "primary" }} />

		<PropControl prop="name" path={path} />
		ID: {item.id}<br />		

		<PropControlDataItem itemType="Agency" path={path} prop="agencyId" label="Agency" />

		<PropControl label="Provenance" help="Where does this list come from? What pre-filtering has been done?" path={path} prop="provenance" type="textarea" item={item} />

		<PropControl prop="priority" path={path} type="number" label />
		
		<Label>Measure</Label>
		<section id="update-measure" className="py-3 px-1 mb-3 border rounded">
		{item?.rows && <p className="alert alert-info">Current coverage in this list: {getCompletedPercentage(item.rows)} </p>}
		<Misc.SubmitButton url={getEndPoint(item, "measure")}>Send to Measure</Misc.SubmitButton>
		<Misc.SubmitButton disabled={item.updating} className="ml-3" url={getEndPoint(item, "update")}>{item.updating ? "List Updating..." : "Send to Update and Measure"}</Misc.SubmitButton>
		
		{debugMode && <>
			<Misc.SubmitButton className="ml-3" url={getEndPoint(item, "clean")}>DEBUG: Cleanup</Misc.SubmitButton>
			<Misc.SubmitButton disabled={!item.updating} className="ml-3" url={getEndPoint(item, "update", true)}>DEBUG: Force Update</Misc.SubmitButton>
		</>}

		{/* TODO 1. Block update button when update is ongoing 2. Allow email update when update is finished. */}
		<p><b className="text-danger">Important notes:</b> Send to Update might return a timeout error. The co2pm field is indeed updating even if this page return an error. Update could take hours to finish if the list is long. You can close or leave this page while the process is loading in our backend server. </p>
		</section>

		{item.rows && <QuantileFilter rows={item.rows} name={item.name} />}

		{ ! item.rows && <>
		<span>CSV Must have two columns only: domain and impressions. </span>
		<a href="/data/publist_example.csv">Download Example CSV</a>
		<PropControlUpload type="spreadsheetUpload" prop="upload" endpoint={getEndPoint(item, "upload")} onFileSelect={validateCSV} onUpload={handleUpload} noUrl />
		</>}
		
		{item.rows && item.headers && 
		<SimpleTable dataArray={item.rows} columns={item.headers} rowsPerPage={50} hasCsv="top" hasFilter/>}

		<PropControl label="Notes" path={path} prop="notes" type="textarea" item={item} />

		{/* eslint-disable-next-line no-alert, no-restricted-globals */}
		<SavePublishDeleteEtc type={C.TYPES.PubList} id={item.id} preDelete={() => confirm("Delete this Publisher List?")}/>
	</div>
	);
}


export default PubListPage;
